// ids.js
// ID generation utility

/**
 * Build a UUIDv4 string from 16 random bytes, per RFC 4122 §4.4 (set the
 * version nibble to 4 and the variant nibble to 8/9/a/b).
 * @param {Uint8Array} bytes 16 random bytes
 * @returns {string}
 */
function formatUUIDv4FromBytes(bytes) {
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return [
        hex.slice(0, 4).join(''),
        hex.slice(4, 6).join(''),
        hex.slice(6, 8).join(''),
        hex.slice(8, 10).join(''),
        hex.slice(10, 16).join(''),
    ].join('-');
}

/**
 * Math.random()-based UUIDv4. Not cryptographically secure — last-resort
 * fallback for environments with no Web Crypto API at all.
 * @returns {string}
 */
function generateUUIDFromMathRandom() {
    const bytes = new Uint8Array(16);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = (Math.random() * 256) | 0;
    }
    return formatUUIDv4FromBytes(bytes);
}

/**
 * Generate a cryptographically-random UUIDv4 (e.g. 'bc681f45-91c2-4a3e-8b7d-2f6a9c1e0d4b').
 *
 * Prefers crypto.randomUUID(), falls back to building a UUIDv4 from
 * crypto.getRandomValues() when randomUUID isn't available (older browsers/test
 * environments), and as a last resort falls back to a Math.random()-based UUIDv4
 * if no Web Crypto API exists at all.
 * @returns {string} A UUIDv4 string
 */
export function generateUUID() {
    const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined; // eslint-disable-line no-undef

    if (cryptoObj?.randomUUID) {
        return cryptoObj.randomUUID();
    }

    if (cryptoObj?.getRandomValues) {
        const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
        return formatUUIDv4FromBytes(bytes);
    }

    // eslint-disable-next-line no-console
    console.warn('[ids.js] Web Crypto API unavailable — falling back to Math.random() for UUID generation (not cryptographically secure).');
    return generateUUIDFromMathRandom();
}
